import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  CircleAlert,
  ChevronLeft,
  ChevronRight,
  Clock3,
  ClipboardPlus,
  Eye,
  LoaderCircle,
  MessageCircle,
  MessagesSquare,
  MapPin,
  MoreVertical,
  PackageSearch,
  Search,
  SearchX,
  Send,
  X,
} from 'lucide-react';
import EmptyState from '../components/EmptyState';
import Header from '../components/Header';
import LoadingIndicator from '../components/LoadingIndicator';
import UserSectionSidebar from '../components/UserSectionSidebar';
import ConfirmModal from '../components/ConfirmModal';
import ImageViewer from '../components/ImageViewer';
import ReportChatModal from '../components/ReportChatModal';
import SellerOrderFormModal, { createEmptySellerOrderForm } from '../components/SellerOrderFormModal';
import { useTranslations } from '../translations';
import { useNotification } from '../components/NotificationProvider';
import { createClientMessageId, fetchUserChats, fetchChatMessages, fetchChatProduct, markChatAsViewed, replyToChat, deleteChat, reportChat, MAX_CHAT_MESSAGE_LENGTH, type ChatConversationRole } from '../services/chat.api';
import { ApiError } from '../services/api.error';
import { createSellerOrder, fetchSellerOrderProductOptions } from '../services/seller-order.api';
import {
  Chat,
  ChatProductDetail,
  ChatMessage,
  SellerOrderProductOption,
  SellerOrderRequest,
} from '../services/api.types';
import { getAccessToken, getUserId } from '../services/auth';
import { useUnreadMessages } from '../contexts/UnreadMessagesContext';
import { chatSocketService } from '../services/chat.socket';
import { createPageUrl } from '../utils/appRoutes';
import { canCreateOrderFromChatProduct, toSellerOrderProductOption } from '../utils/chatOrder';
import { formatUnitType } from '../utils/unitType';
import { getPublishedAt } from '../utils/productDate';
import { getUserPublicName } from '../utils/userSeo';
import './Messages.css';

const COMPACT_DETAILS_BREAKPOINT = 1500;
const MOBILE_MESSAGES_BREAKPOINT = 968;
const COMPOSER_ORDER_TOOLTIP_ID = 'composer-order';
const IOS_KEYBOARD_OPEN_CLASS = 'user-message-ios-keyboard-open';
const MESSAGES_VIEW_HISTORY_KEY = 'roadeMessagesView';

type MessagesMobileView = 'list' | 'chat' | 'product';

const getHistoryState = (): Record<string, unknown> => {
  const state = window.history.state;
  return state && typeof state === 'object' ? state as Record<string, unknown> : {};
};

const createMessagesUrlFromCurrentParams = (
  updateParams: (params: URLSearchParams) => void,
) => {
  const params = new URLSearchParams(window.location.search);
  updateParams(params);
  return createPageUrl('messages', Object.fromEntries(params.entries()));
};

const isIOSDevice = () => (
  /iPad|iPhone|iPod/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
);

interface MessagesProps {
  isLoggedIn: boolean;
  isAuthChecking: boolean;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onNavigateToHome: () => void;
  onLogout: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToLocations?: () => void;
  onNavigateToMyAnnouncements?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToCreateProduct?: () => void;
  onNavigateToProductDetail?: (id: string) => void;
  onNavigateToUserDetails?: (id: string, publicName?: string) => void;
  productId?: string | null;
  initialSearchTerm?: string;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToCookies?: () => void;
}

const CHAT_PAGE_SIZE = 50;
const MESSAGES_PAGE_SIZE = 50;
const normalizeChatSearchTerm = (value: string) => value.trim().replace(/\s+/g, ' ');

interface ChatOrderDraft {
  products: SellerOrderProductOption[];
  form: SellerOrderRequest;
  deliveryLocation: ChatProductDetail['locationDto'];
}

const sortChatsByLatest = (chats: Chat[]) => {
  return [...chats].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateB - dateA;
  });
};

const mergeChatsById = (existingChats: Chat[], incomingChats: Chat[]) => {
  const chatsById = new Map(existingChats.map(chat => [chat.id, chat]));

  incomingChats.forEach(chat => {
    chatsById.set(chat.id, { ...chatsById.get(chat.id), ...chat });
  });

  return sortChatsByLatest([...chatsById.values()]);
};

const toChronologicalMessages = (messages: ChatMessage[]) => {
  return [...messages].sort((a, b) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
};

const mergeMessagesById = (existingMessages: ChatMessage[], incomingMessages: ChatMessage[]) => {
  const messagesById = new Map(existingMessages.map(message => [message.id, message]));

  incomingMessages.forEach(message => {
    messagesById.set(message.id, { ...messagesById.get(message.id), ...message });
  });

  return [...messagesById.values()].sort((a, b) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
};

const isDifferentCalendarDay = (previousDate: string, currentDate: string) => {
  return new Date(previousDate).toDateString() !== new Date(currentDate).toDateString();
};

const Messages: React.FC<MessagesProps> = ({
  isLoggedIn,
  isAuthChecking,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onLogout,
  onNavigateToProfile,
  onNavigateToLocations,
  onNavigateToMyAnnouncements,
  onNavigateToSettings,
  onNavigateToCreateProduct,
  onNavigateToProductDetail,
  onNavigateToUserDetails,
  productId = null,
  initialSearchTerm = '',
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
}) => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const { checkUnreadMessages } = useUnreadMessages();

  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ChatProductDetail | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [productLoading, setProductLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [nextBeforeCreatedAt, setNextBeforeCreatedAt] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [chatsPage, setChatsPage] = useState(1);
  const [chatsTotalPages, setChatsTotalPages] = useState(1);
  const [isLoadingMoreChats, setIsLoadingMoreChats] = useState(false);
  const [chatsTotalElements, setChatsTotalElements] = useState(0);
  const [chatProductId, setChatProductId] = useState<string | null>(productId);
  const [chatSearchInput, setChatSearchInput] = useState(initialSearchTerm);
  const [chatSearchTerm, setChatSearchTerm] = useState(
    normalizeChatSearchTerm(initialSearchTerm)
  );
  const [chatConversationRole, setChatConversationRole] = useState<ChatConversationRole | null>(() => {
    const role = new URLSearchParams(window.location.search).get('conversationRole');
    return role === 'SELLER' || role === 'BUYER' ? role : null;
  });
  const [isFilteringChats, setIsFilteringChats] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [preparingOrderChatId, setPreparingOrderChatId] = useState<string | null>(null);
  const [orderDraft, setOrderDraft] = useState<ChatOrderDraft | null>(null);
  const [openOrderTooltipId, setOpenOrderTooltipId] = useState<string | null>(null);
  const [orderTooltip, setOrderTooltip] = useState<{
    text: string;
    style: React.CSSProperties;
  } | null>(null);
  const [mobileView, setMobileView] = useState<MessagesMobileView>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const chatId = urlParams.get('chatId');
    return chatId ? 'chat' : 'list';
  });
  const [openMenuChatId, setOpenMenuChatId] = useState<string | null>(null);
  const [chatToDelete, setChatToDelete] = useState<string | null>(null);
  const [chatToReport, setChatToReport] = useState<Chat | null>(null);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [isSocketConnected, setIsSocketConnected] = useState(chatSocketService.isConnected());
  const messagesListRef = useRef<HTMLDivElement>(null);
  const chatsListRef = useRef<HTMLDivElement>(null);
  const chatsSidebarRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const chatsRef = useRef<Chat[]>([]);
  const chatsBeingMarkedViewedRef = useRef(new Set<string>());
  const isInitialLoad = useRef(false);
  const isLoadingOlderMessages = useRef(false);
  const savedScrollPosition = useRef(0);
  const previousScrollHeight = useRef(0);
  const shouldAutoScroll = useRef(false);
  const ignoreNextSendButtonClick = useRef(false);
  const sendingMessageRef = useRef(false);
  const pendingReplyRef = useRef<{ chatId: string; message: string; clientMessageId: string } | null>(null);
  const hasLoadedChatsRef = useRef(false);
  const chatsRequestIdRef = useRef(0);

  useEffect(() => {
    const scrollY = window.scrollY;
    const originalBodyPosition = document.body.style.position;
    const originalBodyTop = document.body.style.top;
    const originalBodyLeft = document.body.style.left;
    const originalBodyRight = document.body.style.right;
    const originalBodyWidth = document.body.style.width;
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyOverscrollBehavior = document.body.style.overscrollBehavior;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalHtmlOverscrollBehavior = document.documentElement.style.overscrollBehavior;

    window.scrollTo(0, 0);
    document.body.style.position = 'fixed';
    document.body.style.top = '0';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';

    return () => {
      document.body.style.position = originalBodyPosition;
      document.body.style.top = originalBodyTop;
      document.body.style.left = originalBodyLeft;
      document.body.style.right = originalBodyRight;
      document.body.style.width = originalBodyWidth;
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.overscrollBehavior = originalBodyOverscrollBehavior;
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.documentElement.style.overscrollBehavior = originalHtmlOverscrollBehavior;
      window.scrollTo(0, scrollY);
    };
  }, []);

  const scrollMessagesToBottom = (behavior: ScrollBehavior = 'auto') => {
    const messagesList = messagesListRef.current;
    if (!messagesList) {
      return;
    }

    if (typeof messagesList.scrollTo === 'function') {
      messagesList.scrollTo({
        top: messagesList.scrollHeight,
        behavior
      });
      return;
    }

    messagesList.scrollTop = messagesList.scrollHeight;
  };

  const scheduleScrollMessagesToBottom = (behavior: ScrollBehavior = 'auto') => {
    requestAnimationFrame(() => {
      scrollMessagesToBottom(behavior);
      requestAnimationFrame(() => scrollMessagesToBottom('auto'));
      window.setTimeout(() => scrollMessagesToBottom('auto'), 100);
    });
  };

  useEffect(() => {
    let updateFrame: number | null = null;
    let previousViewportHeight = 0;
    let previousViewportOffsetTop = 0;
    let restingViewportHeight = window.visualViewport?.height ?? window.innerHeight;

    const applyViewportMetrics = () => {
      updateFrame = null;
      const visualViewport = window.visualViewport;
      const viewportHeight = visualViewport?.height ?? window.innerHeight;
      const viewportOffsetTop = visualViewport?.offsetTop ?? 0;
      const messageInputFocused = document.activeElement === messageInputRef.current;
      const viewportChanged = viewportHeight !== previousViewportHeight
        || viewportOffsetTop !== previousViewportOffsetTop;

      if (!messageInputFocused) {
        restingViewportHeight = viewportHeight;
      }

      const iosKeyboardOpen = isIOSDevice()
        && messageInputFocused
        && restingViewportHeight - viewportHeight > 80;

      document.documentElement.style.setProperty('--user-message-viewport-height', `${viewportHeight}px`);
      document.documentElement.style.setProperty('--user-message-viewport-offset-top', `${viewportOffsetTop}px`);
      document.documentElement.classList.toggle(IOS_KEYBOARD_OPEN_CLASS, iosKeyboardOpen);
      previousViewportHeight = viewportHeight;
      previousViewportOffsetTop = viewportOffsetTop;

      if (viewportChanged && messageInputFocused) {
        scheduleScrollMessagesToBottom();
      }
    };

    const updateViewportMetrics = () => {
      if (updateFrame !== null) {
        cancelAnimationFrame(updateFrame);
      }
      updateFrame = requestAnimationFrame(applyViewportMetrics);
    };

    applyViewportMetrics();
    window.addEventListener('resize', updateViewportMetrics);
    window.visualViewport?.addEventListener('resize', updateViewportMetrics);
    window.visualViewport?.addEventListener('scroll', updateViewportMetrics);

    return () => {
      if (updateFrame !== null) {
        cancelAnimationFrame(updateFrame);
      }
      window.removeEventListener('resize', updateViewportMetrics);
      window.visualViewport?.removeEventListener('resize', updateViewportMetrics);
      window.visualViewport?.removeEventListener('scroll', updateViewportMetrics);
      document.documentElement.style.removeProperty('--user-message-viewport-height');
      document.documentElement.style.removeProperty('--user-message-viewport-offset-top');
      document.documentElement.classList.remove(IOS_KEYBOARD_OPEN_CLASS);
    };
  }, []);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  useEffect(() => {
    const handleMessagesHistoryNavigation = () => {
      if (window.innerWidth > MOBILE_MESSAGES_BREAKPOINT || window.location.pathname !== '/messages') {
        return;
      }

      const chatId = new URLSearchParams(window.location.search).get('chatId');
      if (!chatId) {
        setMobileView('list');
        return;
      }

      const historyChat = chatsRef.current.find(chat => chat.id === chatId);
      if (historyChat) {
        setSelectedChat(historyChat);
      }
      setMobileView('chat');
    };

    window.addEventListener('popstate', handleMessagesHistoryNavigation);
    return () => window.removeEventListener('popstate', handleMessagesHistoryNavigation);
  }, []);

  useEffect(() => {
    if (!openOrderTooltipId) {
      return;
    }

    const closeOrderTooltipOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element) {
        const orderAction = target.closest<HTMLElement>('[data-order-tooltip-id]');
        if (orderAction?.dataset.orderTooltipId === openOrderTooltipId) {
          return;
        }
      }
      setOpenOrderTooltipId(null);
      setOrderTooltip(null);
    };

    const closeOrderTooltip = () => {
      setOpenOrderTooltipId(null);
      setOrderTooltip(null);
    };

    document.addEventListener('pointerdown', closeOrderTooltipOnOutsidePointer);
    document.addEventListener('scroll', closeOrderTooltip, true);
    window.addEventListener('resize', closeOrderTooltip);
    window.visualViewport?.addEventListener('resize', closeOrderTooltip);
    return () => {
      document.removeEventListener('pointerdown', closeOrderTooltipOnOutsidePointer);
      document.removeEventListener('scroll', closeOrderTooltip, true);
      window.removeEventListener('resize', closeOrderTooltip);
      window.visualViewport?.removeEventListener('resize', closeOrderTooltip);
    };
  }, [openOrderTooltipId]);

  useEffect(() => {
    setChatProductId(productId);
    setChatSearchInput(initialSearchTerm);
    setChatSearchTerm(normalizeChatSearchTerm(initialSearchTerm));
  }, [initialSearchTerm, productId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setChatSearchTerm(normalizeChatSearchTerm(chatSearchInput));
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [chatSearchInput]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (chatProductId) {
      urlParams.set('productId', chatProductId);
    } else {
      urlParams.delete('productId');
    }
    if (chatSearchTerm) {
      urlParams.set('searchTerm', chatSearchTerm);
    } else {
      urlParams.delete('searchTerm');
    }
    if (chatConversationRole) {
      urlParams.set('conversationRole', chatConversationRole);
    } else {
      urlParams.delete('conversationRole');
    }
    window.history.replaceState(
      getHistoryState(),
      '',
      createPageUrl('messages', Object.fromEntries(urlParams.entries()))
    );
  }, [chatProductId, chatSearchTerm, chatConversationRole]);

  async function refreshFirstChatsPage() {
    const requestId = chatsRequestIdRef.current;
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        return;
      }

      const response = await fetchUserChats(
        1,
        CHAT_PAGE_SIZE,
        chatProductId,
        chatSearchTerm,
        chatConversationRole
      );
      if (requestId !== chatsRequestIdRef.current) {
        return;
      }

      setChats(prevChats => mergeChatsById(prevChats, response.content));
      setChatsTotalPages(response.totalPages);
      setChatsTotalElements(response.totalElements);
    } catch (error) {
      console.error('Error refreshing chats:', error);
    }
  }

  async function refreshSelectedChatMessages(chatId: string) {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        return;
      }

      const chatDetails = await fetchChatMessages(chatId, MESSAGES_PAGE_SIZE);
      const newMessages = toChronologicalMessages(chatDetails.messages.content);

      setMessages(prevMessages => {
        if (prevMessages.length === 0) {
          return newMessages;
        }

        const isUserAtBottom = messagesListRef.current ?
          messagesListRef.current.scrollHeight - messagesListRef.current.scrollTop - messagesListRef.current.clientHeight < 100 : false;
        const hasNewMessages = newMessages.some(newMessage =>
          !prevMessages.some(existingMessage => existingMessage.id === newMessage.id)
        );

        if (hasNewMessages && isUserAtBottom) {
          shouldAutoScroll.current = true;
        }

        return mergeMessagesById(prevMessages, newMessages);
      });

      if (chatDetails.viewedStatus === 'NOT_VIEWED') {
        await markOpenChatAsViewed(chatId);
      }
    } catch (error) {
      console.error('Error refreshing selected chat messages:', error);
    }
  }

  useEffect(() => {
    if (isAuthChecking) {
      return;
    }
    if (!isLoggedIn) {
      onNavigateToLogin();
      return;
    }
    const userId = getUserId();
    setCurrentUserId(userId);

    const loadChats = async () => {
      const requestId = ++chatsRequestIdRef.current;
      const isInitialRequest = !hasLoadedChatsRef.current;
      if (isInitialRequest) {
        setLoading(true);
      } else {
        setIsFilteringChats(true);
      }
      setIsLoadingMoreChats(false);

      try {
        const accessToken = getAccessToken();
        if (!accessToken) {
          onNavigateToLogin();
          return;
        }

        const response = await fetchUserChats(
          1,
          CHAT_PAGE_SIZE,
          chatProductId,
          chatSearchTerm,
          chatConversationRole
        );
        if (requestId !== chatsRequestIdRef.current) {
          return;
        }

        const sortedChats = sortChatsByLatest(response.content);
        setChats(sortedChats);
        requestAnimationFrame(() => {
          if (chatsListRef.current) {
            chatsListRef.current.scrollTop = 0;
          }
          if (chatsSidebarRef.current) {
            chatsSidebarRef.current.scrollTop = 0;
          }
        });
        setChatsTotalPages(response.totalPages);
        setChatsTotalElements(response.totalElements);
        setChatsPage(response.pageNumber);

        if (sortedChats.length > 0) {
          const urlParams = new URLSearchParams(window.location.search);
          const chatId = urlParams.get('chatId');
          const defaultChat = window.innerWidth > MOBILE_MESSAGES_BREAKPOINT
            ? sortedChats[0]
            : null;

          if (chatId) {
            const chatToSelect = sortedChats.find(chat => chat.id === chatId);
            setSelectedChat(chatToSelect || defaultChat);
          } else if (chatProductId) {
            const chatToSelect = sortedChats.find(chat => chat.productId === chatProductId);
            setSelectedChat(chatToSelect || defaultChat);
          } else {
            setSelectedChat(defaultChat);
          }
        } else {
          setSelectedChat(null);
          setSelectedProduct(null);
          setMessages([]);
          setMobileView('list');
        }
      } catch (error) {
        console.error('Error fetching chats:', error);
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
          onNavigateToLogin();
          return;
        }
      } finally {
        if (requestId === chatsRequestIdRef.current) {
          hasLoadedChatsRef.current = true;
          setLoading(false);
          setIsFilteringChats(false);
        }
      }
    };

    loadChats();
  }, [isLoggedIn, onNavigateToLogin, chatProductId, isAuthChecking, chatSearchTerm, chatConversationRole]);

  useEffect(() => {
    if (!isLoggedIn) {
      setIsSocketConnected(false);
      return;
    }

    let previousConnected = chatSocketService.isConnected();
    const unsubscribe = chatSocketService.subscribeStatus((connected) => {
      setIsSocketConnected(connected);
      if (connected && !previousConnected) {
        void refreshFirstChatsPage();
        if (selectedChat) {
          void refreshSelectedChatMessages(selectedChat.id);
        }
      }
      previousConnected = connected;
    });

    return unsubscribe;
  }, [isLoggedIn, chatProductId, selectedChat?.id, chatSearchTerm, chatConversationRole]);

  useEffect(() => {
    if (!isLoggedIn || isSocketConnected) {
      return;
    }

    const pollChats = async () => {
      const requestId = chatsRequestIdRef.current;
      try {
        const accessToken = getAccessToken();
        if (!accessToken) {
          return;
        }

        const response = await fetchUserChats(
          1,
          CHAT_PAGE_SIZE,
          chatProductId,
          chatSearchTerm,
          chatConversationRole
        );
        if (requestId !== chatsRequestIdRef.current) {
          return;
        }

        setChats(prevChats => mergeChatsById(prevChats, response.content));
        setChatsTotalPages(response.totalPages);
        setChatsTotalElements(response.totalElements);
      } catch (error) {
        console.error('Error polling chats:', error);
      }
    };

    const interval = setInterval(pollChats, 60000);

    return () => clearInterval(interval);
  }, [isLoggedIn, chatProductId, isSocketConnected, chatSearchTerm, chatConversationRole]);

  useEffect(() => {
    if (!selectedChat) return;

    const urlParams = new URLSearchParams(window.location.search);
    const currentChatId = urlParams.get('chatId');

    if (currentChatId !== selectedChat.id) {
      urlParams.delete('page');
      if (chatProductId) {
        urlParams.set('productId', chatProductId);
      }
      urlParams.set('chatId', selectedChat.id);
      window.history.replaceState(
        getHistoryState(),
        '',
        createPageUrl('messages', Object.fromEntries(urlParams.entries()))
      );
    }

    const loadChatDetails = async () => {
      setMessagesLoading(true);
      setHasMoreMessages(false);
      setNextBeforeCreatedAt(null);
      setMessages([]);
      setSelectedMessageId(null);
      isLoadingOlderMessages.current = false;
      try {
        const accessToken = getAccessToken();
        if (!accessToken) {
          onNavigateToLogin();
          return;
        }

        const chatDetails = await fetchChatMessages(selectedChat.id, MESSAGES_PAGE_SIZE);
        isInitialLoad.current = true;
        setMessages(toChronologicalMessages(chatDetails.messages.content));
        setHasMoreMessages(chatDetails.messages.hasMore);
        setNextBeforeCreatedAt(chatDetails.messages.nextBeforeCreatedAt);
        if (chatDetails.viewedStatus === 'NOT_VIEWED') {
          await markOpenChatAsViewed(selectedChat.id);
        }

        // Update chat viewed status in local state
        setChats(prevChats =>
          sortChatsByLatest(
            prevChats.map(chat =>
              chat.id === selectedChat.id
                ? { ...chat, viewedStatus: 'VIEWED' }
                : chat
            )
          )
        );
      } catch (error) {
        console.error('Error fetching chat details:', error);
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
          onNavigateToLogin();
          return;
        }
      } finally {
        setMessagesLoading(false);
      }
    };

    loadChatDetails();
  }, [selectedChat, onNavigateToLogin]);

  useEffect(() => {
    if (!selectedChat || isSocketConnected) return;

    const pollMessages = async () => {
      try {
        const accessToken = getAccessToken();
        if (!accessToken) {
          return;
        }

        const chatDetails = await fetchChatMessages(selectedChat.id, MESSAGES_PAGE_SIZE);
        const newMessages = toChronologicalMessages(chatDetails.messages.content);

        setMessages(prevMessages => {
          if (prevMessages.length === 0) {
            return newMessages;
          }

          const lastLoadedMessage = prevMessages[prevMessages.length - 1];
          const hasNewMessages = newMessages.length > 0 &&
            newMessages[newMessages.length - 1].id !== lastLoadedMessage.id;
          const messagesById = new Map(newMessages.map(message => [message.id, message]));
          const messagesWithUpdatedStatuses = prevMessages.map(existingMessage => {
            const updatedMessage = messagesById.get(existingMessage.id);
            return updatedMessage ? { ...existingMessage, ...updatedMessage } : existingMessage;
          });

          if (hasNewMessages) {
            // Check if user is at the bottom of the messages list
            const isUserAtBottom = messagesListRef.current ?
              messagesListRef.current.scrollHeight - messagesListRef.current.scrollTop - messagesListRef.current.clientHeight < 100 : false;

            if (isUserAtBottom) {
              shouldAutoScroll.current = true;
            }

            const latestMessage = newMessages[newMessages.length - 1];
            setChats(prevChats =>
              sortChatsByLatest(
                prevChats.map(chat =>
                  chat.id === selectedChat.id
                    ? {
                        ...chat,
                        lastMessage: latestMessage.message,
                        createdAt: latestMessage.createdAt,
                        viewedStatus: 'VIEWED'
                      }
                    : chat
                )
              )
            );

            const trulyNewMessages = newMessages.filter(newMsg => {
              return !prevMessages.some(existingMsg => existingMsg.id === newMsg.id);
            });

            return mergeMessagesById(messagesWithUpdatedStatuses, trulyNewMessages);
          }

          return messagesWithUpdatedStatuses;
        });

        if (chatDetails.viewedStatus === 'NOT_VIEWED') {
          await markOpenChatAsViewed(selectedChat.id);
        }

        setChats(prevChats =>
          sortChatsByLatest(
            prevChats.map(chat =>
              chat.id === selectedChat.id
                ? { ...chat, viewedStatus: 'VIEWED' }
                : chat
            )
          )
        );
      } catch (error) {
        console.error('Error polling messages:', error);
      }
    };

    const interval = setInterval(pollMessages, 30000);

    return () => clearInterval(interval);
  }, [selectedChat, isSocketConnected]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const unsubscribe = chatSocketService.subscribe((event) => {
      if (event.type === 'MESSAGE_CREATED' && event.message) {
        const isOpenChat = selectedChat?.id === event.chatId;
        if (isOpenChat) {
          const isUserAtBottom = messagesListRef.current ?
            messagesListRef.current.scrollHeight - messagesListRef.current.scrollTop - messagesListRef.current.clientHeight < 100 : false;
          if (isUserAtBottom) {
            shouldAutoScroll.current = true;
          }

          setMessages(prevMessages => mergeMessagesById(prevMessages, [event.message!]));
          void markOpenChatAsViewed(event.chatId);
        }

        const existingChat = chatsRef.current.find(chat => chat.id === event.chatId);
        if (!existingChat) {
          void refreshFirstChatsPage();
        } else {
          setChats(prevChats =>
            sortChatsByLatest(
              prevChats.map(chat =>
                chat.id === event.chatId
                  ? {
                      ...chat,
                      lastMessage: event.message!.message,
                      createdAt: event.lastMessageAt || event.message!.createdAt,
                      viewedStatus: isOpenChat ? 'VIEWED' : 'NOT_VIEWED'
                    }
                  : chat
              )
            )
          );
        }
      }

      if (event.type === 'CHAT_VIEWED' && selectedChat?.id === event.chatId) {
        setMessages(prevMessages =>
          prevMessages.map(message =>
            message.senderId === currentUserId
              ? { ...message, deliveryStatus: 'SEEN' }
              : message
          )
        );
      }
    });

    return unsubscribe;
  }, [isLoggedIn, selectedChat?.id, currentUserId, chatProductId, chatSearchTerm, chatConversationRole]);

  // Handle scroll position after messages update
  useEffect(() => {
    if (!messagesListRef.current || messages.length === 0) return;

    if (isInitialLoad.current) {
      // Scroll to bottom on initial load
      scheduleScrollMessagesToBottom();
      isInitialLoad.current = false;
    } else if (shouldAutoScroll.current) {
      // Auto-scroll to bottom when new messages arrive
      scheduleScrollMessagesToBottom();
      shouldAutoScroll.current = false;
    } else if (isLoadingOlderMessages.current) {
      // Restore scroll position after loading older messages
      requestAnimationFrame(() => {
        if (messagesListRef.current) {
          const newScrollHeight = messagesListRef.current.scrollHeight;
          const heightDifference = newScrollHeight - previousScrollHeight.current;
          // Maintain position by adjusting for new content added at the top
          messagesListRef.current.scrollTop = savedScrollPosition.current + heightDifference;

          // Add a small delay before allowing new scroll events to trigger loading
          setTimeout(() => {
            isLoadingOlderMessages.current = false;
          }, 100);
        }
      });
    }
  }, [messages]);

  useEffect(() => {
    if (mobileView !== 'chat' || messagesLoading || messages.length === 0) {
      return;
    }

    scheduleScrollMessagesToBottom();
  }, [mobileView, selectedChat?.id, messagesLoading]);

  useEffect(() => {
    if (!selectedChat) return;

    const loadProduct = async () => {
      setProductLoading(true);
      setCurrentImageIndex(0);
      setSelectedProduct(null);
      try {
        const product = await fetchChatProduct(selectedChat.id);
        setSelectedProduct(product);
      } catch (error) {
        console.error('Error fetching product:', error);
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
          onNavigateToLogin();
          return;
        }
      } finally {
        setProductLoading(false);
      }
    };

    loadProduct();
  }, [selectedChat]);

  const handleScroll = async () => {
    if (!messagesListRef.current || isLoadingMore || isLoadingOlderMessages.current || !hasMoreMessages || !nextBeforeCreatedAt) return;

    const { scrollTop } = messagesListRef.current;

    // Check if scrolled to the top (to load older messages)
    // Use a smaller threshold to avoid premature triggering
    if (scrollTop <= 50) {
      const previousHeight = messagesListRef.current.scrollHeight;
      const previousScrollTop = scrollTop;
      setIsLoadingMore(true);

      try {
        const accessToken = getAccessToken();
        if (!accessToken || !selectedChat) return;

        const chatDetails = await fetchChatMessages(selectedChat.id, MESSAGES_PAGE_SIZE, nextBeforeCreatedAt);

        if (chatDetails.messages.content.length === 0) {
          setHasMoreMessages(false);
          setIsLoadingMore(false);
          return;
        }

        isLoadingOlderMessages.current = true;
        savedScrollPosition.current = previousScrollTop;
        previousScrollHeight.current = previousHeight;

        setMessages(prev => mergeMessagesById(prev, toChronologicalMessages(chatDetails.messages.content)));
        setHasMoreMessages(chatDetails.messages.hasMore);
        setNextBeforeCreatedAt(chatDetails.messages.nextBeforeCreatedAt);
      } catch (error) {
        console.error('Error loading more messages:', error);
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
          onNavigateToLogin();
          return;
        }
      } finally {
        setIsLoadingMore(false);
      }
    }
  };

  const handleChatsScroll = async () => {
    const scrollContainer = window.innerWidth <= MOBILE_MESSAGES_BREAKPOINT
      ? chatsSidebarRef.current
      : chatsListRef.current;

    if (!scrollContainer || isLoadingMoreChats || chatsPage >= chatsTotalPages) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

    if (scrollTop + clientHeight >= scrollHeight - 50) {
      setIsLoadingMoreChats(true);
      const requestId = chatsRequestIdRef.current;
      try {
        const accessToken = getAccessToken();
        if (!accessToken) return;

        const nextPage = chatsPage + 1;
        const response = await fetchUserChats(
          nextPage,
          CHAT_PAGE_SIZE,
          chatProductId,
          chatSearchTerm,
          chatConversationRole
        );
        if (requestId !== chatsRequestIdRef.current) {
          return;
        }

        setChats(prev => mergeChatsById(prev, response.content));
        setChatsPage(response.pageNumber);
        setChatsTotalPages(response.totalPages);
        setChatsTotalElements(response.totalElements);
      } catch (error) {
        console.error('Error loading more chats:', error);
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
          onNavigateToLogin();
          return;
        }
      } finally {
        setIsLoadingMoreChats(false);
      }
    }
  };

  useEffect(() => {
    const messagesList = messagesListRef.current;
    if (!messagesList) return;

    messagesList.addEventListener('scroll', handleScroll);
    return () => messagesList.removeEventListener('scroll', handleScroll);
  }, [hasMoreMessages, nextBeforeCreatedAt, isLoadingMore, selectedChat]);

  useEffect(() => {
    const chatsList = chatsListRef.current;
    const chatsSidebar = chatsSidebarRef.current;
    const scrollContainers = [chatsList, chatsSidebar].filter(
      (container): container is HTMLDivElement => container !== null,
    );

    scrollContainers.forEach(container => container.addEventListener('scroll', handleChatsScroll));
    return () => scrollContainers.forEach(container => container.removeEventListener('scroll', handleChatsScroll));
  }, [chatsPage, chatsTotalPages, isLoadingMoreChats, chatSearchTerm, chatConversationRole, loading, chats.length]);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > COMPACT_DETAILS_BREAKPOINT) {
        // Reset to show all views on desktop
        setMobileView('list');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePreviousImage = () => {
    if (selectedProduct && selectedProduct.images.length > 0 && currentImageIndex > 0) {
      setCurrentImageIndex((prev) => prev - 1);
    }
  };

  const handleNextImage = () => {
    if (selectedProduct && selectedProduct.images.length > 0 && currentImageIndex < selectedProduct.images.length - 1) {
      setCurrentImageIndex((prev) => prev + 1);
    }
  };

  const openConversation = (chat: Chat) => {
    setSelectedChat(chat);

    if (window.innerWidth <= MOBILE_MESSAGES_BREAKPOINT) {
      const chatUrl = createMessagesUrlFromCurrentParams(params => {
        params.set('chatId', chat.id);
      });
      window.history.pushState(
        { ...getHistoryState(), [MESSAGES_VIEW_HISTORY_KEY]: 'chat' },
        '',
        chatUrl,
      );
    }

    setMobileView('chat');
  };

  const returnToConversationList = () => {
    if (
      window.innerWidth <= MOBILE_MESSAGES_BREAKPOINT
      && getHistoryState()[MESSAGES_VIEW_HISTORY_KEY] === 'chat'
    ) {
      window.history.back();
      return;
    }

    const listUrl = createMessagesUrlFromCurrentParams(params => {
      params.delete('chatId');
    });
    window.history.replaceState(
      { ...getHistoryState(), [MESSAGES_VIEW_HISTORY_KEY]: 'list' },
      '',
      listUrl,
    );
    setMobileView('list');
  };

  async function markOpenChatAsViewed(chatId: string) {
    if (chatsBeingMarkedViewedRef.current.has(chatId)) {
      return;
    }
    chatsBeingMarkedViewedRef.current.add(chatId);
    try {
      await markChatAsViewed(chatId);
      setChats(prevChats =>
        sortChatsByLatest(
          prevChats.map(chat =>
            chat.id === chatId
              ? { ...chat, viewedStatus: 'VIEWED' }
              : chat
          )
        )
      );
      await checkUnreadMessages();
    } catch (error) {
      console.error('Error marking chat as viewed:', error);
    } finally {
      chatsBeingMarkedViewedRef.current.delete(chatId);
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    
    const isToday = date.toDateString() === now.toDateString();
    
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();
    
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    if (isToday) {
      return `Azi, ${hours}:${minutes}`;
    }
    
    if (isYesterday) {
      return `Ieri, ${hours}:${minutes}`;
    }
    
    const day = date.getDate();
    const monthNames = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Noi', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    const currentYear = now.getFullYear();
    if (year === currentYear) {
      return `${day} ${month}, ${hours}:${minutes}`;
    }
    
    return `${day} ${month} ${year}, ${hours}:${minutes}`;
  };

  const formatMessageDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const monthNames = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day} ${month} ${year} ${hours}:${minutes}`;
  };

  const handleMessageSelect = (messageId: string) => {
    setSelectedMessageId(prevMessageId => prevMessageId === messageId ? null : messageId);
  };

  const formatSelectedMessageDetails = (message: ChatMessage, isCurrentUser: boolean) => {
    const sentAt = formatDate(message.createdAt);
    if (!isCurrentUser || !message.deliveryStatus) {
      return sentAt;
    }

    const deliveryStatusLabel = message.deliveryStatus === 'SEEN' ? 'Vazut' : 'Trimis';
    return `${deliveryStatusLabel} - ${sentAt}`;
  };

  const ownsSelectedProduct = Boolean(
    selectedProduct && currentUserId && selectedProduct.user.id === currentUserId,
  );
  const canCreateOrder = Boolean(selectedChat)
    && canCreateOrderFromChatProduct(selectedProduct, currentUserId);
  const canSendMessages = selectedChat?.canSendMessages !== false;
  const sendMessageUnavailable = !canSendMessages || isSendingMessage || !replyMessage.trim();
  const orderUnavailableReason = productLoading
    ? t.messages.orderProductLoading
    : !selectedProduct
      ? t.messages.orderProductUnavailable
      : !ownsSelectedProduct
        ? t.messages.orderOwnerOnly
        : t.messages.orderSaleOnly;

  const prepareOrderForChat = async (chat: Chat, knownProduct: ChatProductDetail | null = null) => {
    if (preparingOrderChatId) return;
    if (!chat.canCreateOrder) {
      return;
    }

    setPreparingOrderChatId(chat.id);
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        onNavigateToLogin();
        return;
      }
      const product = knownProduct?.id === chat.productId
        ? knownProduct
        : await fetchChatProduct(chat.id);
      if (!canCreateOrderFromChatProduct(product, currentUserId)) {
        return;
      }

      const chatUser = chat.user;
      const fallbackProduct = toSellerOrderProductOption(product);
      const products = await fetchSellerOrderProductOptions().catch(() => [fallbackProduct]);
      if (!products.some((option) => option.id === product.id)) {
        products.unshift(fallbackProduct);
      }

      setOrderDraft({
        products,
        form: createEmptySellerOrderForm({
          productId: product.id,
          deliveryLocationId: product.locationDto.id,
          // Only the name shown in the chat is pre-filled; the phone is never copied from the profile (Terms 10.1).
          customerName: `${chatUser.firstName} ${chatUser.lastName}`.trim(),
          unitPrice: product.price.price,
        }),
        deliveryLocation: product.locationDto,
      });
    } catch (error) {
      console.error('Could not prepare seller order from chat', error);
      showNotification(t.messages.orderPreparationError, 'error');
    } finally {
      setPreparingOrderChatId(null);
    }
  };

  const handleOpenOrder = async () => {
    if (!canCreateOrder || !selectedChat || !selectedProduct) {
      return;
    }
    await prepareOrderForChat(selectedChat, selectedProduct);
  };

  const toggleUnavailableOrderTooltip = (
    tooltipId: string,
    tooltipText: string,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    const shouldClose = openOrderTooltipId === tooltipId;
    if (shouldClose) {
      setOpenOrderTooltipId(null);
      setOrderTooltip(null);
      event.currentTarget.blur();
      return;
    }

    const buttonRect = event.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const tooltipWidth = Math.min(260, viewportWidth - 32);
    const estimatedTooltipHeight = 80;
    const availableBelow = viewportHeight - buttonRect.bottom - 24;
    const availableAbove = buttonRect.top - 24;
    const openBelow = availableBelow >= estimatedTooltipHeight || availableBelow >= availableAbove;
    const availableHeight = Math.max(32, openBelow ? availableBelow : availableAbove);
    const centeredLeft = buttonRect.left + (buttonRect.width - tooltipWidth) / 2;
    const left = Math.max(16, Math.min(centeredLeft, viewportWidth - tooltipWidth - 16));

    setOpenOrderTooltipId(tooltipId);
    setOrderTooltip({
      text: tooltipText,
      style: openBelow
        ? { top: buttonRect.bottom + 8, left, width: tooltipWidth, maxHeight: availableHeight }
        : { bottom: viewportHeight - buttonRect.top + 8, left, width: tooltipWidth, maxHeight: availableHeight },
    });
  };

  const closeUnavailableOrderTooltip = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpenOrderTooltipId(null);
    setOrderTooltip(null);
    event.currentTarget.blur();
  };

  const saveChatOrder = async (request: SellerOrderRequest) => {
    try {
      await createSellerOrder(request);
      setOrderDraft(null);
      showNotification(t.orders.saveSuccess, 'success');
    } catch (error) {
      console.error('Could not create seller order from chat', error);
      showNotification(t.orders.saveError, 'error');
      throw error;
    }
  };

  const handleSendMessage = async () => {
    if (sendingMessageRef.current || !replyMessage.trim() || !selectedChat || !canSendMessages) {
      return;
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      onNavigateToLogin();
      return;
    }

    const messageText = replyMessage;
    const pendingReply = pendingReplyRef.current;
    const clientMessageId = pendingReply?.chatId === selectedChat.id && pendingReply.message === messageText
      ? pendingReply.clientMessageId
      : createClientMessageId();
    pendingReplyRef.current = { chatId: selectedChat.id, message: messageText, clientMessageId };
    sendingMessageRef.current = true;
    setIsSendingMessage(true);
    setReplyMessage('');

    if (messageInputRef.current) {
      messageInputRef.current.style.height = 'auto';
    }

    try {
      const sentMessage = await replyToChat(selectedChat.id, {
        message: messageText,
        clientMessageId
      });

      pendingReplyRef.current = null;
      shouldAutoScroll.current = true;
      setMessages(prev => mergeMessagesById(prev, [sentMessage]));

      // Update the chat list to show the new last message
      setChats(prevChats =>
        sortChatsByLatest(
          prevChats.map(chat =>
            chat.id === selectedChat.id
              ? { ...chat, lastMessage: sentMessage.message, createdAt: sentMessage.createdAt }
              : chat
          )
        )
      );

      // Keep focus on input - use requestAnimationFrame for reliable timing
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (messageInputRef.current) {
            messageInputRef.current.focus();
          }
        });
      });
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      setReplyMessage(currentMessage => currentMessage.trim() ? currentMessage : messageText);
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      if (error instanceof ApiError && error.hasErrorCode('chat_participant_blocked')) {
        setSelectedChat(currentChat => currentChat?.id === selectedChat.id
          ? { ...currentChat, canSendMessages: false }
          : currentChat);
        setChats(currentChats => currentChats.map(chat => (
          chat.id === selectedChat.id ? { ...chat, canSendMessages: false } : chat
        )));
        showNotification(t.messages.messagingBlockedNotice, 'error');
        return;
      }
      showNotification(t.messages.errorSending, 'error');
      // Refocus on error too
      requestAnimationFrame(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      });
    } finally {
      sendingMessageRef.current = false;
      setIsSendingMessage(false);
    }
  };

  const handleSendButtonPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (event.pointerType !== 'mouse') {
      messageInputRef.current?.focus({ preventScroll: true });
    }
  };

  const handleSendButtonPointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === 'mouse') {
      return;
    }

    event.preventDefault();
    messageInputRef.current?.focus({ preventScroll: true });
    ignoreNextSendButtonClick.current = true;
    void handleSendMessage();
    window.setTimeout(() => {
      ignoreNextSendButtonClick.current = false;
    }, 0);
  };

  const handleSendButtonClick = () => {
    if (ignoreNextSendButtonClick.current) {
      ignoreNextSendButtonClick.current = false;
      return;
    }

    if (isIOSDevice()) {
      messageInputRef.current?.focus({ preventScroll: true });
    }
    void handleSendMessage();
  };

  const handleMessageInputWrapperClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!canSendMessages) {
      return;
    }
    const target = event.target;
    if (target instanceof Element && target.closest('button, textarea')) {
      return;
    }

    messageInputRef.current?.focus({ preventScroll: true });
  };

  const handleDeleteChat = async () => {
    if (!chatToDelete) return;

    try {
      await deleteChat(chatToDelete);

      setChats(prevChats => prevChats.filter(chat => chat.id !== chatToDelete));

      if (selectedChat?.id === chatToDelete) {
        const remainingChats = chats.filter(chat => chat.id !== chatToDelete);
        setSelectedChat(remainingChats.length > 0 ? remainingChats[0] : null);
        if (remainingChats.length === 0) {
          setMessages([]);
          setSelectedProduct(null);
        }
      }

      await checkUnreadMessages();
      showNotification('Conversația a fost ștearsă cu succes', 'success');
    } catch (error) {
      console.error('Error deleting chat:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      showNotification('Nu s-a putut șterge conversația. Încercați din nou.', 'error');
    } finally {
      setChatToDelete(null);
    }
  };

  const handleReportChatSubmit = async (reason: string, otherText?: string) => {
    if (!chatToReport) return;

    try {
      const reasonMap: Record<string, string> = {
        spam: t.reportChatModal.spam,
        harassment: t.reportChatModal.harassment,
        inappropriateContent: t.reportChatModal.inappropriateContent,
        scam: t.reportChatModal.scam,
        offensiveLanguage: t.reportChatModal.offensiveLanguage,
      };
      const reasonText = reason === 'other' && otherText ? otherText : (reasonMap[reason] || reason);

      await reportChat(chatToReport.id, [reasonText]);
      setChatToReport(null);
      showNotification(t.reportChatModal.successMessage, 'success');
    } catch (error) {
      console.error('Error reporting chat:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      setChatToReport(null);
      showNotification(t.reportChatModal.errorMessage, 'error');
    }
  };

  const toggleMenu = (chatId: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (e.detail > 0) {
      e.currentTarget.blur();
    }
    setOpenMenuChatId(openMenuChatId === chatId ? null : chatId);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuChatId(null);
    };

    if (openMenuChatId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuChatId]);

  const desktopAccountNavigation = (
    <UserSectionSidebar
      activeItem="messages"
      onProfileClick={onNavigateToProfile}
      onLocationsClick={onNavigateToLocations}
      onMessagesClick={() => {}}
      onAnnouncementsClick={onNavigateToMyAnnouncements}
      onSettingsClick={onNavigateToSettings}
    />
  );

  const mobileAccountNavigation = (
    <UserSectionSidebar
      activeItem="messages"
      variant="tabs"
      onProfileClick={onNavigateToProfile}
      onLocationsClick={onNavigateToLocations}
      onMessagesClick={() => {}}
      onAnnouncementsClick={onNavigateToMyAnnouncements}
      onSettingsClick={onNavigateToSettings}
    />
  );

  const chatSearchControl = (
    <div className="user-message-chat-search">
      <Search className="user-message-chat-search-icon" aria-hidden="true" />
      <input
        type="search"
        className="user-message-chat-search-input"
        value={chatSearchInput}
        onChange={(event) => {
          const value = event.target.value;
          setChatSearchInput(value);
          if (!value) {
            setChatProductId(null);
            setChatSearchTerm('');
          }
        }}
        placeholder={t.messages.searchPlaceholder}
        aria-label={t.messages.searchPlaceholder}
        maxLength={255}
      />
      {chatSearchInput && (
        <button
          type="button"
          className="user-message-chat-search-clear"
          onClick={() => {
            setChatProductId(null);
            setChatSearchInput('');
            setChatSearchTerm('');
          }}
          aria-label={t.messages.clearSearch}
          title={t.messages.clearSearch}
        >
          <X aria-hidden="true" />
        </button>
      )}
    </div>
  );

  const chatRoleFilterControl = (
    <div
      className="user-message-role-filter"
      role="group"
      aria-label={t.messages.roleFilterLabel}
    >
      {([
        { value: null, label: t.messages.allRoles },
        { value: 'SELLER' as const, label: t.messages.sellingRole },
        { value: 'BUYER' as const, label: t.messages.buyingRole },
      ] satisfies Array<{ value: ChatConversationRole | null; label: string }>).map(option => (
        <button
          key={option.value ?? 'ALL'}
          type="button"
          className={`user-message-role-filter-option ${chatConversationRole === option.value ? 'user-message-active' : ''}`}
          aria-pressed={chatConversationRole === option.value}
          onClick={() => setChatConversationRole(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  const hasActiveChatFilters = Boolean(chatSearchTerm || chatConversationRole || chatProductId);
  const clearChatFilters = () => {
    setChatProductId(null);
    setChatSearchInput('');
    setChatSearchTerm('');
    setChatConversationRole(null);
  };

  if (loading) {
    return (
      <div className="user-message-page">
        <Header
          isLoggedIn={isLoggedIn}
          onNavigateToLogin={onNavigateToLogin}
          onNavigateToRegister={onNavigateToRegister}
          onNavigateToHome={onNavigateToHome}
          onLogout={onLogout}
          onProfileClick={onNavigateToProfile}
          onLocationsClick={onNavigateToLocations}
          onAnnouncementsClick={onNavigateToMyAnnouncements}
          onSettingsClick={onNavigateToSettings}
          onNavigateToCreateProduct={onNavigateToCreateProduct}
        />
        <div className="user-message-container">
          <aside className="user-message-desktop-account-sidebar">
            {desktopAccountNavigation}
          </aside>
          <div className="user-message-conversations-sidebar" ref={chatsSidebarRef}>
            <div className="user-message-mobile-account-navigation">
              {mobileAccountNavigation}
            </div>
            <h1 className="user-message-conversations-title">{t.messages.title.replace('{count}', '0')}</h1>
            <div className="user-message-conversations-list">
              <LoadingIndicator message={t.messages.loadingConversations} />
            </div>
          </div>

          <div className="user-message-vertical-divider"></div>

          <div className="user-message-conversation-view user-message-empty-state" aria-busy="true">
            <LoadingIndicator message={t.messages.loadingConversation} />
          </div>

          <div className="user-message-vertical-divider"></div>

          <div className="user-message-product-sidebar user-message-empty-state" aria-busy="true">
            <LoadingIndicator message={t.productDetail.loading} />
          </div>
        </div>
      </div>
    );
  }

  if (chats.length === 0) {
    return (
      <div className="user-message-page">
        <Header
          isLoggedIn={isLoggedIn}
          onNavigateToLogin={onNavigateToLogin}
          onNavigateToRegister={onNavigateToRegister}
          onNavigateToHome={onNavigateToHome}
          onLogout={onLogout}
          onProfileClick={onNavigateToProfile}
          onLocationsClick={onNavigateToLocations}
          onAnnouncementsClick={onNavigateToMyAnnouncements}
          onSettingsClick={onNavigateToSettings}
          onNavigateToCreateProduct={onNavigateToCreateProduct}
        />
        <div className="user-message-container">
          <aside className="user-message-desktop-account-sidebar">
            {desktopAccountNavigation}
          </aside>
          <div className="user-message-conversations-sidebar" ref={chatsSidebarRef}>
            <div className="user-message-mobile-account-navigation">
              {mobileAccountNavigation}
            </div>
            <h1 className="user-message-conversations-title">{t.messages.title.replace('{count}', '0')}</h1>
            {chatSearchControl}
            {chatRoleFilterControl}

            <div className="user-message-conversations-list">
              {isFilteringChats ? (
                <LoadingIndicator message={t.messages.loadingConversations} />
              ) : (
                <EmptyState
                  className="user-message-conversations-empty"
                  icon={hasActiveChatFilters ? <SearchX /> : <MessagesSquare />}
                  title={hasActiveChatFilters ? t.messages.noSearchResultsTitle : t.messages.noMessagesTitle}
                  description={hasActiveChatFilters ? t.messages.noSearchResultsDescription : t.messages.noMessagesDescription}
                  headingLevel={2}
                  announce
                >
                  {hasActiveChatFilters && (
                    <button
                      className="app-empty-state-secondary"
                      type="button"
                      onClick={clearChatFilters}
                    >
                      {t.messages.resetFilters}
                    </button>
                  )}
                </EmptyState>
              )}
            </div>
          </div>

          <div className="user-message-vertical-divider"></div>

          <div className="user-message-conversation-view user-message-empty-state">
            <EmptyState
              className="user-message-pane-empty"
              icon={<MessageCircle />}
              title={t.messages.selectConversationTitle}
              description={t.messages.selectConversationDescription}
              headingLevel={2}
            />
          </div>

          <div className="user-message-vertical-divider"></div>

          <div className="user-message-product-sidebar user-message-empty-state">
            <EmptyState
              className="user-message-pane-empty"
              icon={<PackageSearch />}
              title={t.messages.productPlaceholderTitle}
              description={t.messages.productPlaceholderDescription}
              headingLevel={2}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-message-page">
      <Header
        isLoggedIn={isLoggedIn}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onSettingsClick={onNavigateToSettings}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />

      <div className="user-message-container">
        <aside className="user-message-desktop-account-sidebar">
          {desktopAccountNavigation}
        </aside>
        <div className="user-message-conversations-sidebar" ref={chatsSidebarRef}>
          <div className="user-message-mobile-account-navigation">
            {mobileAccountNavigation}
          </div>
          <h1 className="user-message-conversations-title">{t.messages.title.replace('{count}', chatsTotalElements.toString())}</h1>
          {chatSearchControl}
          {chatRoleFilterControl}

          <div
            className={`user-message-conversations-list ${isFilteringChats ? 'user-message-filtering' : ''}`}
            ref={chatsListRef}
            aria-busy={isFilteringChats}
          >
            {isFilteringChats && (
              <LoadingIndicator message={t.messages.loadingConversations} variant="compact" />
            )}
            {chats.map((chat) => (
              <div
                key={chat.id}
                className={`user-message-conversation-item ${selectedChat?.id === chat.id ? 'user-message-active' : ''}`}
                role="button"
                tabIndex={0}
                aria-current={selectedChat?.id === chat.id ? 'true' : undefined}
                aria-label={`${chat.user.firstName} ${chat.user.lastName}: ${chat.lastMessage}`}
                onClick={(event) => {
                  if (event.detail > 0) {
                    event.currentTarget.blur();
                  }
                  openConversation(chat);
                }}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
                    return;
                  }
                  event.preventDefault();
                  openConversation(chat);
                }}
              >
                <div className="user-message-conversation-product-image">
                  {chat.productImageUrl ? (
                    <img src={chat.productImageUrl} alt="" />
                  ) : (
                    <div className="user-message-conversation-product-image-placeholder" />
                  )}
                </div>
                <div className="user-message-conversation-details">
                  <div className="user-message-conversation-header">
                    <h3 className="user-message-conversation-name">{chat.user.firstName} {chat.user.lastName}</h3>
                    <span className={`user-message-conversation-timestamp ${chat.viewedStatus === 'NOT_VIEWED' ? 'user-message-unread' : ''}`}>
                      {formatDate(chat.createdAt)}
                    </span>
                  </div>
                  <div className="user-message-conversation-product-row">
                    <p className={`user-message-conversation-product-title ${chat.viewedStatus === 'NOT_VIEWED' ? 'user-message-unread' : ''}`}>
                      {chat.productTitle}
                    </p>
                    {chat.productSuspensionReason ? (
                      <span className="user-message-expired-badge">{t.messages.blockedAnnouncement}</span>
                    ) : chat.productStatus === 'EXPIRED' && (
                      <span className="user-message-expired-badge">{t.messages.expiredAnnouncement}</span>
                    )}
                  </div>
                  <p className={`user-message-conversation-preview ${chat.viewedStatus === 'NOT_VIEWED' ? 'user-message-unread' : ''}`}>
                    {chat.lastMessage}
                  </p>
                </div>
                <div className="user-message-conversation-actions">
                  <span
                    className={`user-message-conversation-order-action user-message-order-action ${openOrderTooltipId === chat.id ? 'user-message-order-tooltip-open' : ''}`}
                    data-order-tooltip-id={chat.id}
                  >
                    <button
                      type="button"
                      className="user-message-conversation-order-button"
                      aria-label={`${t.messages.addOrder} pentru ${chat.user.firstName} ${chat.user.lastName}`}
                      aria-disabled={!chat.canCreateOrder || preparingOrderChatId !== null}
                      aria-expanded={!chat.canCreateOrder ? openOrderTooltipId === chat.id : undefined}
                      aria-describedby={!chat.canCreateOrder && openOrderTooltipId === chat.id ? `order-tooltip-${chat.id}` : undefined}
                      tabIndex={chat.canCreateOrder && preparingOrderChatId === null ? 0 : -1}
                      title={chat.canCreateOrder ? t.messages.addOrder : undefined}
                      onDoubleClick={(event) => {
                        if (!chat.canCreateOrder) {
                          closeUnavailableOrderTooltip(event);
                        }
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!chat.canCreateOrder) {
                          toggleUnavailableOrderTooltip(chat.id, t.messages.orderListUnavailable, event);
                          return;
                        }
                        setOpenOrderTooltipId(null);
                        void prepareOrderForChat(chat);
                      }}
                    >
                      {preparingOrderChatId === chat.id
                        ? <LoaderCircle className="user-message-order-spinner" size={17} aria-hidden />
                        : <ClipboardPlus size={18} aria-hidden />}
                    </button>
                  </span>
                  <div className="user-message-conversation-menu-container">
                    <button
                      className="user-message-conversation-menu-button"
                      onClick={(e) => toggleMenu(chat.id, e)}
                      aria-label={t.messages.moreOptions}
                    >
                      <MoreVertical size={20} aria-hidden />
                    </button>
                    {openMenuChatId === chat.id && (
                      <div className="user-message-conversation-menu-dropdown">
                        <button
                          className="user-message-conversation-menu-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setChatToReport(chat);
                            setOpenMenuChatId(null);
                          }}
                        >
                          {t.reportChatModal.reportAction}
                        </button>
                        <button
                          className="user-message-conversation-menu-item user-message-conversation-menu-item-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setChatToDelete(chat.id);
                            setOpenMenuChatId(null);
                          }}
                        >
                          Șterge conversația
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {isLoadingMoreChats && (
              <LoadingIndicator message={t.messages.loadingMoreConversations} variant="compact" />
            )}
          </div>
        </div>

        <div className="user-message-vertical-divider"></div>

        <div
          className={`user-message-conversation-view ${mobileView === 'chat' ? 'user-message-active' : ''} ${mobileView === 'product' ? 'user-message-product-open' : ''}`}
        >
          {selectedChat ? (
            <>
              <div
                className={`user-message-conversation-header-bar ${selectedProduct ? 'user-message-product-link-available' : ''}`}
                onClick={() => {
                  if (selectedProduct && window.innerWidth <= COMPACT_DETAILS_BREAKPOINT) {
                    setMobileView('product');
                  }
                }}
              >
                <button
                  className="user-message-mobile-back-button"
                  type="button"
                  aria-label={t.messages.backToConversations}
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    returnToConversationList();
                  }}
                >
                  <ArrowLeft size={21} aria-hidden />
                </button>
                <div className="user-message-conversation-header-product-main-image">
                  {selectedChat.productImageUrl ? (
                    <img src={selectedChat.productImageUrl} alt="" />
                  ) : (
                    <div className="user-message-conversation-header-product-main-placeholder" />
                  )}
                </div>
                <div className="user-message-conversation-header-text">
                  <h2
                    className="user-message-conversation-header-name"
                    onClick={(event) => {
                      event.stopPropagation();
                      onNavigateToUserDetails?.(
                        selectedChat.user.id,
                        getUserPublicName(selectedChat.user),
                      );
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {selectedChat.user.firstName} {selectedChat.user.lastName}
                  </h2>
                  {productLoading ? (
                    <div className="user-message-conversation-header-product user-message-conversation-header-product-loading">
                      <LoaderCircle className="user-message-inline-spinner" size={15} aria-hidden />
                      <span>{t.productDetail.loading}</span>
                    </div>
                  ) : selectedProduct && (
                    <button
                      type="button"
                      className="user-message-conversation-header-product"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (window.innerWidth <= COMPACT_DETAILS_BREAKPOINT) {
                          setMobileView('product');
                        } else {
                          onNavigateToProductDetail?.(selectedProduct.id);
                        }
                      }}
                    >
                      <span className="user-message-conversation-header-product-title">
                        {selectedProduct.title}
                      </span>
                      <ChevronRight className="user-message-conversation-header-product-chevron" size={18} aria-hidden />
                    </button>
                  )}
                </div>
              </div>

              {!canSendMessages ? (
                <div className="user-message-expired-notice" role="status">
                  <CircleAlert size={17} aria-hidden />
                  <span>{t.messages.messagingBlockedNotice}</span>
                </div>
              ) : selectedChat.productSuspensionReason ? (
                <div className="user-message-expired-notice" role="status">
                  <CircleAlert size={17} aria-hidden />
                  <span>{t.messages.blockedAnnouncementNotice}</span>
                </div>
              ) : selectedChat.productStatus === 'EXPIRED' && (
                <div className="user-message-expired-notice" role="status">
                  <CircleAlert size={17} aria-hidden />
                  <span>{t.messages.expiredAnnouncementNotice}</span>
                </div>
              )}

              <div
                className={`user-message-messages-list ${!messagesLoading && messages.length > 0 ? 'user-message-messages-list-bottom-aligned' : ''}`}
                ref={messagesListRef}
                aria-busy={messagesLoading || isLoadingMore}
              >
                {isLoadingMore && (
                  <LoadingIndicator message={t.messages.loadingMore} variant="compact" />
                )}
                {messagesLoading ? (
                  <LoadingIndicator message={t.messages.loadingConversation} />
                ) : messages.length === 0 ? (
                  <EmptyState
                    className="user-message-thread-empty"
                    icon={<MessageCircle />}
                    title={t.messages.emptyConversationTitle}
                    description={t.messages.emptyConversationDescription}
                    headingLevel={2}
                  />
                ) : (
                  messages.map((message, index) => {
                    const isCurrentUser = message.senderId === currentUserId;
                    const showTimestamp = index === 0 ||
                      (index > 0 && isDifferentCalendarDay(messages[index - 1].createdAt, message.createdAt));

                    return (
                      <div
                        key={message.id}
                        className={`user-message-message-wrapper ${isCurrentUser ? 'user-message-message-wrapper-sent' : 'user-message-message-wrapper-received'}`}
                      >
                        {showTimestamp && (
                          <div className="user-message-message-timestamp">{formatMessageDate(message.createdAt)}</div>
                        )}
                        <div
                          className={`user-message-message ${isCurrentUser ? 'user-message-message-sent' : 'user-message-message-received'}`}
                          role="button"
                          tabIndex={0}
                          aria-pressed={selectedMessageId === message.id}
                          onClick={() => handleMessageSelect(message.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleMessageSelect(message.id);
                            }
                          }}
                        >
                          <p className="user-message-message-text">{message.message}</p>
                        </div>
                        {selectedMessageId === message.id && (
                          <time className="user-message-message-sent-at" dateTime={message.createdAt}>
                            {formatSelectedMessageDetails(message, isCurrentUser)}
                          </time>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="user-message-message-input-container">
                <span
                  className={`user-message-order-action ${openOrderTooltipId === COMPOSER_ORDER_TOOLTIP_ID ? 'user-message-order-tooltip-open' : ''}`}
                  data-order-tooltip-id={COMPOSER_ORDER_TOOLTIP_ID}
                >
                  <button
                    type="button"
                    className="user-message-order-button"
                    aria-label={t.messages.addOrder}
                    aria-disabled={!canCreateOrder || preparingOrderChatId !== null}
                    aria-expanded={!canCreateOrder ? openOrderTooltipId === COMPOSER_ORDER_TOOLTIP_ID : undefined}
                    aria-describedby={!canCreateOrder && openOrderTooltipId === COMPOSER_ORDER_TOOLTIP_ID ? `order-tooltip-${COMPOSER_ORDER_TOOLTIP_ID}` : undefined}
                    tabIndex={canCreateOrder ? 0 : -1}
                    title={canCreateOrder ? t.messages.addOrder : undefined}
                    onDoubleClick={(event) => {
                      if (!canCreateOrder) {
                        closeUnavailableOrderTooltip(event);
                      }
                    }}
                    onClick={(event) => {
                      if (!canCreateOrder) {
                        toggleUnavailableOrderTooltip(COMPOSER_ORDER_TOOLTIP_ID, orderUnavailableReason, event);
                        return;
                      }
                      setOpenOrderTooltipId(null);
                      void handleOpenOrder();
                    }}
                  >
                    {preparingOrderChatId === selectedChat.id
                      ? <LoaderCircle className="user-message-order-spinner" size={20} aria-hidden />
                      : <ClipboardPlus size={21} aria-hidden />}
                  </button>
                </span>
                <div
                  className={`user-message-message-input-wrapper ${!canSendMessages ? 'user-message-message-input-wrapper-disabled' : ''}`}
                  onClick={handleMessageInputWrapperClick}
                  aria-disabled={!canSendMessages}
                >
                  <textarea
                    ref={messageInputRef}
                    placeholder={canSendMessages ? t.messages.messagePlaceholder : t.messages.messagingBlockedPlaceholder}
                    className="user-message-message-input"
                    value={replyMessage}
                    maxLength={MAX_CHAT_MESSAGE_LENGTH}
                    disabled={!canSendMessages}
                    onChange={(e) => {
                      if (pendingReplyRef.current?.message !== e.target.value) {
                        pendingReplyRef.current = null;
                      }
                      setReplyMessage(e.target.value);
                      const messagesList = messagesListRef.current;
                      const shouldKeepLatestMessageVisible = messagesList
                        ? messagesList.scrollHeight - messagesList.scrollTop - messagesList.clientHeight < 100
                        : false;
                      // Auto-resize textarea
                      const textarea = e.target;
                      textarea.style.height = 'auto';
                      const lineHeight = 20;
                      const maxLines = 3;
                      const maxHeight = lineHeight * maxLines;
                      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
                      textarea.style.height = newHeight + 'px';
                      if (shouldKeepLatestMessageVisible) {
                        scheduleScrollMessagesToBottom('auto');
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    onFocus={() => scheduleScrollMessagesToBottom()}
                    rows={1}
                  />
                  <div className="user-message-composer-actions">
                    <span className="user-message-message-character-count">
                      {replyMessage.length}/{MAX_CHAT_MESSAGE_LENGTH}
                    </span>
                    <button
                      type="button"
                      className="user-message-send-button"
                      onPointerDown={handleSendButtonPointerDown}
                      onPointerUp={handleSendButtonPointerUp}
                      onClick={handleSendButtonClick}
                      disabled={!canSendMessages}
                      aria-disabled={sendMessageUnavailable}
                      aria-label={t.productDetail.sendMessage}
                    >
                      <Send size={19} aria-hidden />
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <EmptyState
              className="user-message-pane-empty"
              icon={<MessageCircle />}
              title={t.messages.selectConversationTitle}
              description={t.messages.selectConversationDescription}
              headingLevel={2}
            />
          )}
        </div>

        <div className="user-message-vertical-divider"></div>

        <div
          className={`user-message-product-sidebar ${mobileView === 'product' ? 'user-message-active' : ''}`}
          aria-busy={productLoading}
        >
          <button
            className="user-message-mobile-back-button"
            type="button"
            aria-label={t.messages.backToConversation}
            onClick={() => setMobileView('chat')}
          >
            <ArrowLeft size={21} aria-hidden />
          </button>
          {productLoading ? (
            <LoadingIndicator message={t.productDetail.loading} />
          ) : selectedProduct ? (
            <>
              <div className="user-message-product-heading">
                <div className={`user-message-product-type ${selectedProduct.productType === 'DEMAND' ? 'user-message-product-type-demand' : 'user-message-product-type-sell'}`}>
                  <span className="user-message-product-type-caption">{t.productDetail.listingType}</span>
                  <span className="user-message-product-type-value">
                    {selectedProduct.productType === 'DEMAND'
                      ? t.productDetail.wantToBuy
                      : t.productDetail.wantToSell}
                  </span>
                </div>
                <h2
                  className="user-message-message-product-title"
                  onClick={() => onNavigateToProductDetail?.(selectedProduct.id)}
                >
                  {selectedProduct.title}
                </h2>

                <div className="user-message-product-price-row">
                  <span className="user-message-product-price">
                    {selectedProduct.price.price} {selectedProduct.price.currency === 'RON' ? 'lei' : selectedProduct.price.currency}/{formatUnitType(selectedProduct.price.unitType)}
                  </span>
                  {selectedProduct.negotiable && <span className="user-message-product-negotiable">Negociabil</span>}
                </div>
              </div>

              <div className="user-message-product-image-container">
                {selectedProduct.images[currentImageIndex]?.imageUrl ? (
                  <img
                    src={selectedProduct.images[currentImageIndex].imageUrl}
                    alt={selectedProduct.title}
                    className="user-message-product-image"
                    onClick={() => setShowImageViewer(true)}
                    style={{ cursor: 'pointer' }}
                  />
                ) : (
                  <div className="user-message-product-image-placeholder">{t.productCard.noImage}</div>
                )}
                <div className="user-message-image-counter">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path fillRule="evenodd" clipRule="evenodd" d="M2 0H10C11.1027 0 12 0.897333 12 2V10C12 11.1027 11.1027 12 10 12H2C0.897333 12 0 11.1027 0 10V2C0 0.897333 0.897333 0 2 0ZM2 1.33333H10C10.368 1.33333 10.6667 1.632 10.6667 2V7.576L8.53133 5.75467C7.87133 5.19333 6.83867 5.19333 6.18467 5.75067L1.33333 9.79867V2C1.33333 1.632 1.632 1.33333 2 1.33333ZM4.33333 3.66667C4.33333 4.21867 3.88533 4.66667 3.33333 4.66667C2.78133 4.66667 2.33333 4.21867 2.33333 3.66667C2.33333 3.11467 2.78133 2.66667 3.33333 2.66667C3.88533 2.66667 4.33333 3.11467 4.33333 3.66667ZM2.374 10.6667H10C10.368 10.6667 10.6667 10.368 10.6667 10V9.32933L7.666 6.76933C7.50533 6.63133 7.208 6.63067 7.044 6.77L2.374 10.6667Z" fill="white" transform="translate(2, 2)" />
                  </svg>
                  <span>{selectedProduct.images.length > 0 ? currentImageIndex + 1 : 0}/{selectedProduct.images.length}</span>
                </div>
                <button className="user-message-image-nav-button user-message-image-nav-left" type="button" aria-label={t.messages.previousImage} onClick={handlePreviousImage} disabled={currentImageIndex === 0}>
                  <ChevronLeft size={18} aria-hidden />
                </button>
                <button className="user-message-image-nav-button user-message-image-nav-right" type="button" aria-label={t.messages.nextImage} onClick={handleNextImage} disabled={selectedProduct.images.length === 0 || currentImageIndex === selectedProduct.images.length - 1}>
                  <ChevronRight size={18} aria-hidden />
                </button>
              </div>

              <div className="user-message-product-info-section">
                <div className="user-message-product-info-dual-row">
                  <div className="user-message-product-info-row">
                    <MapPin size={18} aria-hidden />
                    <span className="user-message-product-info-text">{selectedProduct.locationDto.name}, jud. {selectedProduct.locationDto.county.name}</span>
                  </div>
                  <div className="user-message-product-info-row">
                    <Clock3 size={18} aria-hidden />
                    <span className="user-message-product-info-text">Postat {formatDate(getPublishedAt(selectedProduct))}</span>
                  </div>
                </div>
                <div className="user-message-product-info-row">
                  <Eye size={18} aria-hidden />
                  <span className="user-message-product-info-text">
                    {t.productCard.viewsCompact.replace('{count}', selectedProduct.viewCount.toString())}
                  </span>
                </div>
              </div>

              <section className="user-message-product-description-section">
                <h3 className="user-message-product-description-title">{t.messages.productDetails}</h3>
                <p className="user-message-product-description">{selectedProduct.description}</p>
              </section>
            </>
          ) : (
            <EmptyState
              className="user-message-pane-empty"
              icon={<PackageSearch />}
              title={t.messages.productPlaceholderTitle}
              description={t.messages.productPlaceholderDescription}
              headingLevel={2}
            />
          )}
        </div>
      </div>

      {openOrderTooltipId && orderTooltip && createPortal((
        <div
          id={`order-tooltip-${openOrderTooltipId}`}
          className="user-message-order-tooltip"
          role="tooltip"
          style={orderTooltip.style}
        >
          {orderTooltip.text}
        </div>
      ), document.body)}

      {orderDraft && (
        <SellerOrderFormModal
          title={t.messages.addOrder}
          products={orderDraft.products}
          initialForm={orderDraft.form}
          initialDeliveryLocation={orderDraft.deliveryLocation}
          onClose={() => setOrderDraft(null)}
          onSubmit={saveChatOrder}
        />
      )}

      {chatToDelete && (
        <ConfirmModal
          title="Șterge conversația"
          message="Ești sigur că vrei să ștergi această conversație? Această acțiune este permanentă și nu poate fi anulată."
          confirmText="Șterge"
          destructive
          onCancel={() => setChatToDelete(null)}
          onConfirm={handleDeleteChat}
        />
      )}

      {chatToReport && (
        <ReportChatModal
          onClose={() => setChatToReport(null)}
          onSubmit={handleReportChatSubmit}
        />
      )}

      {showImageViewer && selectedProduct && selectedProduct.images.length > 0 && (
        <ImageViewer
          images={selectedProduct.images.map(img => img.imageUrl)}
          initialIndex={currentImageIndex}
          onClose={() => setShowImageViewer(false)}
        />
      )}
    </div>
  );
};

export default Messages;
